import { useParams } from "react-router-dom";
import {
  useAddCreditsMutation,
  useDeleteUserAccountMutation,
  useGetUserInfoQuery,
  useCancelSubscriptionMutation,
  useGetAllDraftsQuery,
} from "../store/base-api";
import PageError from "../components/errors/page-error";
import LoadingIndicator from "../components/loaders/loading-indicator";
import StoryListItem from "../components/list-items/story-list-item";
import { Fragment, useState } from "react";
import {
  blockInvalidChar,
  env,
  formatDate,
  getGenderString,
  PURCHASE_CATEGORIES,
  splitOnUppercase,
  STORY_CATEGORIES,
} from "../utils";
import { BaseError } from "../models";
import FormError from "../components/errors/form-error";
import PurchaseListItem from "../components/list-items/purchase-list-item";
import toast from "react-hot-toast";
import SearchBar from "../components/inputs/search-bar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import PostPaidUsageListItem from "../components/list-items/post-paid-usage-list-item";
import CreditSpendsTable from "../components/list-items/credit-spends-table";
import DraftListItem from "../components/list-items/draft-list-item";

function ContentCategoryPanel({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="shrink-0 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white px-4 py-3.5">
        <span className="text-base font-semibold text-slate-900">
          {title}{" "}
          <span className="tabular-nums font-semibold text-slate-700">
            ({count})
          </span>
        </span>
      </div>
      <div className="max-h-[350px] min-h-[100px] overflow-y-auto bg-slate-100/90 px-2 py-2">
        <div className="flex flex-col gap-2">{children}</div>
      </div>
    </div>
  );
}

export default function UserInfo() {
  const { id } = useParams();
  const [searchText, setSearchText] = useState("");
  const {
    data,
    isLoading,
    error: fetchError,
  } = useGetUserInfoQuery(id!, { skip: !id });
  const {
    data: draftsData,
    isLoading: draftsLoading,
    error: draftsError,
  } = useGetAllDraftsQuery({ id: id!, pageSize: 1000 }, { skip: !id });
  const [deleteUser] = useDeleteUserAccountMutation();
  const [addCredits] = useAddCreditsMutation();
  const [cancelSubscription, { isLoading: cancelingSubscription }] =
    useCancelSubscriptionMutation();

  const [creditsToAdd, setCreditsToAdd] = useState(0);

  const [deleting, setDeleting] = useState(false);
  const [addingCredits, setAddingCredits] = useState(false);
  const [error, setError] = useState<BaseError>();
  const [cancelSubscriptionError, setCancelSubscriptionError] =
    useState<BaseError>();

  const publishedStoryCount = data?.webStories?.length ?? 0;
  const publishedBookCount =
    data?.webStories?.filter((s) => s.storyType === 0).length ?? 0;
  const publishedVideoCount =
    data?.webStories?.filter(
      (s) => s.storyType === 1 || s.storyType === 2,
    ).length ?? 0;
  const draftItemCount = draftsData?.items?.length ?? 0;

  const getObjectValue = (key: string, value: any) => {
    switch (key) {
      case "created":
        return formatDate(value);
      case "gender":
        return getGenderString(value);
      case "subscriptionCreatedDate":
        return formatDate(value);
      case "subscriptionStartDate":
        return formatDate(value);
      case "subscriptionEndDate":
        return formatDate(value);
      case "subscriptionNextRefreshDate":
        return formatDate(value);
      default:
        return String(value);
    }
  };

  const deleteUserHandler = async () => {
    if (
      deleting ||
      !id ||
      !window.confirm("Are you sure you want to delete this user?")
    )
      return;
    setDeleting(true);

    try {
      await deleteUser(id).unwrap();
    } catch (e: any) {
      setError(e);
    } finally {
      setDeleting(false);
    }
  };

  const addCreditsHandler = async () => {
    if (
      addingCredits ||
      !id ||
      creditsToAdd < 1 ||
      !data?.email ||
      !window.confirm(`Are you sure you want to add ${creditsToAdd} credits?`)
    )
      return;

    setAddingCredits(true);
    try {
      const addCreditsPromise = addCredits({
        userId: id,
        credits: creditsToAdd,
        creditType: "Credits",
      }).unwrap();

      toast.promise(addCreditsPromise, {
        loading: "Adding credits...",
        success: `Successfully added ${creditsToAdd} credits`,
        error: (err) => `Error: ${"Failed to add credits"}`,
      });

      await addCreditsPromise;
    } catch (e: any) {
      setError(e);
    } finally {
      setAddingCredits(false);
      // window.location.reload();
    }
  };

  const cancelSubscriptionHandler = async () => {
    if (
      cancelingSubscription ||
      !id ||
      !window.confirm(
        "Are you sure you want to cancel this user's subscription?",
      )
    )
      return;
    try {
      await cancelSubscription(id).unwrap();
      toast.success("Subscription cancelled successfully");
    } catch (e: any) {
      setCancelSubscriptionError(e);
      toast.error("Failed to cancel subscription");
    }
  };

  const handleSearch = (text: string) => {
    // Navigate to the users route with search params
    window.location.href = `/users?search=${encodeURIComponent(text)}&currentPage=1`;
  };

  if (fetchError) return <PageError error={fetchError} />;

  return (
    <div className="flex flex-1 w-full p-4 overflow-y-auto max-h-[calc(100vh-96px)]">
      <div className="flex p-8 flex-col flex-1 bg-white border-2 border-neutral-50 rounded-2xl min-h-[calc(100vh-128px)] h-max shadow-xl">
        <div className="sticky z-10 flex mb-4">
          <SearchBar
            searchText={searchText}
            setSearchText={setSearchText}
            onSearch={handleSearch}
            placeholder="Search users by email..."
          />
        </div>
        {isLoading || !data ? (
          <div className="flex-1 grid place-items-center">
            <LoadingIndicator />
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <h3 className="text-2xl font-semibold">User Info</h3>

              <div className="flex gap-4 justify-between flex-wrap">
                <div className="flex-1 grid auto-rows-min gap-x-8 gap-y-2 grid-cols-[max-content,_1fr]">
                  {Object.entries(data)
                    .filter(
                      ([key]) =>
                        key !== "webStories" &&
                        key !== "pdfPurchases" &&
                        key !== "walletBalance" &&
                        key !== "postPaidUsages" &&
                        key !== "storyBookAllowance" &&
                        key !== "videoAllowance",
                    )
                    .map(([key, value]) => (
                      <Fragment key={`user-info-${key}`}>
                        <p className="font-semibold text-lg capitalize">
                          {splitOnUppercase(key)}
                        </p>
                        <div>{getObjectValue(key, value)}</div>
                      </Fragment>
                    ))}

                  {/* Profile URL Field */}
                  {(data as any).profileName && (
                    <Fragment key="user-profile-url">
                      <p className="font-semibold text-lg capitalize">
                        Profile URL
                      </p>
                      <div>
                        <a
                          href={`${env("app_url")}users/${(data as any).profileName}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {`${env("app_url")}users/${(data as any).profileName}`}
                        </a>
                      </div>
                    </Fragment>
                  )}
                </div>

                <div className="flex flex-col gap-4 w-1/4 border border-slate-500 rounded-lg p-4 h-fit">
                  <p className="font-semibold text-lg capitalize">
                    Add Credits
                  </p>

                  <Fragment key={`user-info-add-credits`}>
                    <div className="flex flex-col gap-2">
                      <label htmlFor="creditsAmount" className="font-medium">
                        Amount
                      </label>
                      <input
                        id="creditsAmount"
                        type="text"
                        pattern="[0-9]*"
                        min="0"
                        placeholder="Add Credits..."
                        value={creditsToAdd === 0 ? "" : creditsToAdd}
                        className={`border-2 border-neutral-200 rounded-md p-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value === "" || /^\d+$/.test(value)) {
                            setCreditsToAdd(value === "" ? 0 : Number(value));
                          }
                        }}
                        onKeyDown={blockInvalidChar}
                        onPaste={(e) => {
                          const pastedData = e.clipboardData.getData("text");
                          if (!/^\d+$/.test(pastedData)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                    <button
                      className="btn-primary"
                      disabled={addingCredits}
                      onClick={addCreditsHandler}
                    >
                      Add Funds
                    </button>
                  </Fragment>
                </div>
              </div>

              {data.email !== "anonymous@storybird.ai" ? (
                <>
                  <FormError error={error} />
                  <FormError error={cancelSubscriptionError} />
                  <button
                    className="btn-warning"
                    disabled={cancelingSubscription}
                    onClick={cancelSubscriptionHandler}
                  >
                    Cancel Subscription
                  </button>
                  <button
                    className="btn-error"
                    disabled={deleting}
                    onClick={deleteUserHandler}
                  >
                    Delet{deleting ? "ing" : "e"} User
                  </button>
                </>
              ) : null}
            </div>
            <div className="flex flex-col gap-6 rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/80 p-6 shadow-md ring-1 ring-slate-200/60">
              <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">
                    Library
                  </p>
                  <h3
                    id="content-section-title"
                    className="text-2xl font-bold tracking-tight text-slate-900"
                  >
                    Content
                  </h3>
                  <p className="max-w-xl text-sm leading-relaxed text-slate-600">
                    Published work and drafts, grouped by format.
                  </p>
                </div>
                <div
                  className="flex flex-wrap gap-2 sm:justify-end"
                  role="status"
                  aria-label="Content counts summary"
                >
                  <span className="inline-flex items-baseline gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm">
                    <span className="tabular-nums text-lg font-bold text-slate-900">
                      {publishedStoryCount}
                    </span>
                    <span className="text-slate-600">published</span>
                  </span>
                  <span className="inline-flex items-baseline gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm">
                    <span className="tabular-nums font-semibold text-slate-800">
                      {publishedBookCount}
                    </span>
                    <span className="text-slate-600">books</span>
                  </span>
                  <span className="inline-flex items-baseline gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm">
                    <span className="tabular-nums font-semibold text-slate-800">
                      {publishedVideoCount}
                    </span>
                    <span className="text-slate-600">videos</span>
                  </span>
                  <span className="inline-flex items-baseline gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm shadow-sm">
                    <span className="tabular-nums text-lg font-bold text-indigo-900">
                      {draftItemCount}
                    </span>
                    <span className="text-indigo-800">drafts</span>
                  </span>
                </div>
              </header>

              <section
                className="space-y-4"
                aria-labelledby="content-published-heading"
              >
                <div className="border-l-4 border-indigo-500 pl-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Published
                  </p>
                  <h4
                    id="content-published-heading"
                    className="text-xl font-bold text-slate-900"
                  >
                    Stories & videos
                  </h4>
                  <p className="mt-0.5 text-sm text-slate-600">
                    Live content visible to readers.
                  </p>
                </div>
                {data.webStories && data.webStories.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {STORY_CATEGORIES.map((category) => {
                      const categoryStories =
                        data.webStories?.filter((story) =>
                          category.id.includes(story.storyType),
                        ) || [];

                      return (
                        <ContentCategoryPanel
                          key={category.value}
                          title={category.title}
                          count={categoryStories.length}
                        >
                          {categoryStories.length > 0 ? (
                            categoryStories.map((story, i) => (
                              <StoryListItem
                                key={`${category.value}-story-${i + 1}`}
                                {...{ ...story, user: data }}
                              />
                            ))
                          ) : (
                            <p className="px-2 py-3 text-sm text-slate-500">
                              No {category.title.toLowerCase()} found.
                            </p>
                          )}
                        </ContentCategoryPanel>
                      );
                    })}
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-4 py-6 text-center text-slate-700">
                    This user has no published stories or videos.
                  </p>
                )}
              </section>

              <section
                className="space-y-4 border-t border-slate-200 pt-6"
                aria-labelledby="content-drafts-heading"
              >
                <div className="border-l-4 border-amber-500 pl-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    In progress
                  </p>
                  <h4
                    id="content-drafts-heading"
                    className="text-xl font-bold text-slate-900"
                  >
                    Drafts
                  </h4>
                  <p className="mt-0.5 text-sm text-slate-600">
                    Unpublished work by format.
                  </p>
                </div>
                {draftsLoading ? (
                  <div className="flex justify-center p-6">
                    <LoadingIndicator />
                  </div>
                ) : draftsError ? (
                  <p className="text-red-600">
                    Error loading drafts: {draftsError.toString()}
                  </p>
                ) : draftsData &&
                  draftsData.items &&
                  draftsData.items.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {STORY_CATEGORIES.map((category) => {
                      const categoryDrafts =
                        draftsData.items?.filter((draft) =>
                          category.id.includes(draft.storyType),
                        ) || [];

                      return (
                        <ContentCategoryPanel
                          key={category.value}
                          title={category.title}
                          count={categoryDrafts.length}
                        >
                          {categoryDrafts.length > 0 ? (
                            categoryDrafts.map((draft, i) => (
                              <DraftListItem
                                key={`${category.value}-draft-${i + 1}`}
                                {...draft}
                              />
                            ))
                          ) : (
                            <p className="px-2 py-3 text-sm text-slate-500">
                              No {category.title.toLowerCase()} drafts found.
                            </p>
                          )}
                        </ContentCategoryPanel>
                      );
                    })}
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-4 py-6 text-center text-slate-700">
                    This user has no drafts.
                  </p>
                )}
              </section>
            </div>
            <div>
              <h3 className="text-2xl mb-4 font-semibold">Purchases</h3>
              {data.pdfPurchases && data.pdfPurchases.length > 0 ? (
                <Accordion type="multiple" className="w-full">
                  <div className="grid grid-cols-2 gap-4">
                    {PURCHASE_CATEGORIES.map((category) => {
                      const categoryPurchases =
                        data.pdfPurchases?.filter(
                          (purchase) =>
                            purchase.creditSpendType === category.id,
                        ) || [];

                      return (
                        <AccordionItem
                          key={category.value}
                          value={category.value}
                          className="w-full"
                        >
                          <AccordionTrigger className="text-xl font-medium shadow-xl border border-input bg-slate-100 p-4 rounded-lg">
                            {category.title} ({categoryPurchases.length})
                          </AccordionTrigger>
                          <AccordionContent className="max-h-[250px] bg-slate-200 border border-solid rounded-lg overflow-scroll px-2">
                            <div className="flex flex-col gap-2 pt-2">
                              {categoryPurchases.length > 0 ? (
                                categoryPurchases.map((purchase, i) => (
                                  <PurchaseListItem
                                    key={`${category.value}-purchase-${i + 1}`}
                                    {...purchase}
                                  />
                                ))
                              ) : (
                                <p className="text-gray-500 p-2">
                                  No {category.title.toLowerCase()} found.
                                </p>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </div>
                </Accordion>
              ) : (
                <p className="text-black">This user has no PDF purchases.</p>
              )}
            </div>
            <div>
              <h3 className="text-2xl mb-4 font-semibold">Credit Spends</h3>
              <CreditSpendsTable userId={id!} />
            </div>
            <div>
              <h3 className="text-2xl mb-4 font-semibold">Post-Paid Usage</h3>
              {data.postPaidUsages && data.postPaidUsages.length > 0 ? (
                <Accordion type="single" collapsible className="w-1/2">
                  <AccordionItem value="post-paid-usage" className="w-full">
                    <AccordionTrigger className="text-xl font-medium shadow-xl border border-input bg-slate-100 p-4 rounded-lg">
                      All Usage Records ({data.postPaidUsages.length})
                    </AccordionTrigger>
                    <AccordionContent className="max-h-[350px] bg-slate-200 border border-solid rounded-lg overflow-scroll px-2">
                      <div className="flex flex-col gap-2 pt-2">
                        {data.postPaidUsages.map((usage, i) => (
                          <PostPaidUsageListItem
                            key={`post-paid-usage-${i + 1}`}
                            index={i}
                            {...usage}
                          />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ) : (
                <p className="text-black">
                  This user has no post-paid usage records.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
